/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import InputRegistration from "pages/Front/Registration/components/InputRegistration";
import Birthdate from "pages/Front/Registration/components/Birthdate";
import ButtonRegistration, {
    ButtonRegistrationStyle,
    ButtonRegistrationType
} from "pages/Front/Registration/components/ButtonRegistration";
import {useDispatch} from "useDispatch";
import {useTypedSelector} from "helpers/reducers";
import {UserType} from "types/enums/UserType";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {AccountType} from "types/enums/AccountType";
import * as yup from "yup";
import {useHistory} from "react-router-dom";
import RegistrationAction from "store/registration/RegistrationAction";
import {toast} from "react-toastify";
import {DEFAULT_HALL_ID} from "helpers/config";
import {closeModal} from "helpers/actions";
import {Driver} from "types/entities/Driver";

const AccountNormalSchema = yup.object().shape({
    home_hall_id: yup.number().required(),
    name: yup.string().required(),
    last_name: yup.string().required(),
    nickname: yup.string().required(),
    birthdate: yup.string().required(),
    email: yup.string().required(),
    phone: yup.string().required(),
});

const AccountGuestSchema = yup.object().shape({
    home_hall_id: yup.number().required(),
    name: yup.string().required(),
    last_name: yup.string().required(),
    nickname: yup.string().required(),
    birthdate: yup.string().required(),
    parent_user_id: yup.number().required(),
});

interface RegistrationFormProps {
    type: AccountType;
    linkClose?: string;
    linkSuccess: string;
    guestId?: number;
    defaultValues: Driver;
    itemRefs?: any;
    onFocus?: (inputName: string) => void;
}

const RegistrationForm = (props: RegistrationFormProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const accounts = useTypedSelector(state => state.registrationState.accounts);
    // const prepareGroup = useTypedSelector(state => state.shoppingState.prepareGroup);
    const mainAccount = accounts && accounts.find(account => account.user_type === UserType.User);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const {
        register,
        handleSubmit,
        setValue,
        formState: {errors},
    } = useForm({
        resolver: yupResolver(props.type === AccountType.Guest ? AccountGuestSchema : AccountNormalSchema),
        defaultValues: props.defaultValues,
    });
    const inputIsDisabled = mainAccount && props.type === AccountType.Main;

    const onCloseModalHandler = () => {
        dispatch(closeModal());
    };

    const onSubmitError = data => {
        console.log(data);
    };

    const registerAccount = (data, withLogin: boolean) => {
        return new Promise(async (resolve, reject) => {
            try {
                if (data.parent_user_id === null || data.parent_user_id === undefined || data.parent_user_id === "") {
                    //handle null on parent_user_id
                    delete data.parent_user_id;
                }
                console.log(data);
                if (isSubmitting) {
                    reject(new Error("Already submitting"));
                }
                const user = await dispatch(RegistrationAction.fetchSetAccount(data, withLogin));
                if (user) {
                    // const driverPreparedGroup: DriverPrepareGroupRequest = {
                    //     driver_id: user.id,
                    //     prepared_group_id: prepareGroup.id,
                    // };
                    // dispatch(ShoppingAction.fetchSetDriverToPreparedGroup(driverPreparedGroup));

                    const successMessage = withLogin ? "Account created." : "Guest created.";
                    if (!withLogin) {
                        dispatch(closeModal());
                    }
                    toast.success(t(successMessage));
                    resolve(user);
                } else {
                    reject(new Error("Data not processed"));
                }
            } catch (error: any | Error) {
                const errorMessage = error?.data?.message || "Unknown error occurred";
                // toast.error(errorMessage);
                //
                // const errorMessages: any[] = error?.data?.data?.messages;
                // if (errorMessages) {
                //     Object.values(errorMessages).map(err => {
                //         toast.error(err[err.length - 1]);
                //     });
                // }
                reject(new Error(errorMessage));
            }
        }).catch(err => {
            // Handle any unhandled promise rejections here
            console.error("Unhandled promise rejection:");
            console.error(err);
        });
    };

    const onSubmitHandler = data => {
        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);

        if (props.type === AccountType.Main) {
            return registerAccount(data, true).then(r => {
                history.push(props.linkSuccess);
                setIsSubmitting(false);
            });
        }
        if (props.type === AccountType.Guest && !props.guestId) {
            return registerAccount(data, false).then(r => {
                history.push(props.linkSuccess);
                setIsSubmitting(false);
            });
        }
        if (props.type === AccountType.Guest && props.guestId) {
            console.log("Edit guest...");
            console.log(data);
            dispatch(RegistrationAction.fetchUpdateGuest(data))
                .then(r => {
                    console.log("Edit guest - response then");
                    console.log(r);
                    toast.success(t("Edited."));
                    history.push(props.linkSuccess);
                    setIsSubmitting(false);
                    dispatch(closeModal());
                })
                .catch(err => {
                    console.log("Edit guest - err catch");
                    console.log(err);
                    toast.error(err);
                    setIsSubmitting(false);
                });
        }
    };

    return (
        <form className={"RegistrationForm w-full"} onSubmit={handleSubmit(onSubmitHandler, onSubmitError)}>
            <div className="grid w-full grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-6">
                {/*@ts-ignore*/}
                <input {...register("home_hall_id")} type={"hidden"} value={DEFAULT_HALL_ID} />
                <input {...register("account_type")} type={"hidden"} value={props.type} />
                <input {...register("parent_user_id")} type={"hidden"} value={props.type === AccountType.Guest ? mainAccount?.id : null} />

                <InputRegistration
                    className={"sm:col-span-3"}
                    disabled={inputIsDisabled}
                    errors={errors}
                    label={"First name"}
                    name={"name"}
                    placeholder={"Alexander"}
                    register={register}
                    required={true}
                />
                <InputRegistration
                    className={"sm:col-span-3"}
                    disabled={inputIsDisabled}
                    errors={errors}
                    label={"Last name"}
                    name={"last_name"}
                    placeholder={"Kramsky"}
                    register={register}
                    required={true}
                />
                <InputRegistration
                    className={"sm:col-span-3"}
                    disabled={inputIsDisabled}
                    errors={errors}
                    label={"Nickname"}
                    name={"nickname"}
                    placeholder={"Sano"}
                    register={register}
                    required={true}
                />

                <div className={"sm:col-span-3"}>
                    <Birthdate
                        defaultValues={props.defaultValues}
                        disabled={inputIsDisabled}
                        errors={errors}
                        label={t("Birthdate")}
                        name={"birthdate"}
                        register={register}
                        required={true}
                        setValue={setValue}
                    />
                </div>

                <InputRegistration
                    className={"sm:col-span-3"}
                    disabled={inputIsDisabled}
                    errors={errors}
                    label={"Email"}
                    name={"email"}
                    placeholder={"info@bomboclaat.com"}
                    register={register}
                    required={props.type === AccountType.Main ? true : false}
                    type={"email"}
                />
                <InputRegistration
                    className={"sm:col-span-3"}
                    disabled={inputIsDisabled}
                    errors={errors}
                    label={"Phone"}
                    name={"phone"}
                    placeholder={"+420911123456"}
                    register={register}
                    required={props.type === AccountType.Main ? true : false}
                    type={"tel"}
                />

                <div className={"sm:col-span-6"}>
                    {props.type === AccountType.Main && !mainAccount && (
                        <div className={"flex justify-center mb-6"}>
                            <ButtonRegistration
                                loading={isSubmitting}
                                style={ButtonRegistrationStyle.Primary}
                                type={ButtonRegistrationType.Submit}
                                onClick={() => onSubmitHandler}
                            >
                                <span>{t("Create account")}</span>
                            </ButtonRegistration>
                        </div>
                    )}
                    {props.type === "guest" && (
                        <div className={"flex flex-col-reverse sm:flex-row justify-between mb-6"}>
                            <ButtonRegistration style={ButtonRegistrationStyle.Gray} type={ButtonRegistrationType.Button} onClick={onCloseModalHandler}>
                                <span>{t("Close")}</span>
                            </ButtonRegistration>
                            <ButtonRegistration
                                className={"mb-6 sm:mb-0"}
                                loading={isSubmitting}
                                style={ButtonRegistrationStyle.Primary}
                                type={ButtonRegistrationType.Submit}
                                onClick={() => onSubmitHandler}
                            >
                                <span>{props.guestId ? t("Edit guest") : t("Add guest")}</span>
                            </ButtonRegistration>
                        </div>
                    )}
                </div>
            </div>
        </form>
    );
};

export default RegistrationForm;
